/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import React, {FocusEventHandler, InputHTMLAttributes, KeyboardEventHandler, PureComponent} from 'react';
import {ChangeFunction, ErrorType, OrnamentShape} from '../util/interfaces';
import {roundTo} from '../util/miscUtil';
import DvrdInputController from "./dvrdInputController";

interface Props {
    onChange: ChangeFunction<string | number>;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
    onEnter?: KeyboardEventHandler;
    value: string | number;
    label?: string;
    ornaments?: OrnamentShape | OrnamentShape[];
    inputProps?: InputHTMLAttributes<any>;
    disabled?: boolean;
    autoFocus?: boolean;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id?: string;
    placeholder?: string;
    multipleSeparators: boolean;
    wholeNumbers: boolean;
    asNumber?: boolean;
    asCurrency: boolean;
    autoSelect?: boolean;
    unControlled?: boolean;
}

// TODO Add option to disable symbols (+,-)

interface State {
    value: number | string;
}

export default class DvrdNumberInput extends PureComponent<Props, State> {
    static defaultProps = {
        asCurrency: false,
        wholeNumbers: false,
        multipleSeparators: false,
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            value: props.value,
        };
    }


    onKeyDown = (evt: React.KeyboardEvent) => {
        const {onKeyDown, asCurrency, wholeNumbers, inputProps, asNumber, multipleSeparators} = this.props;
        const {value} = this.state;
        const {key, target} = evt;
        let numValue = Number(value);
        // Allow only number and simple edit keys
        if (!/\d|Backspace|ArrowRight|ArrowLeft|ArrowUp|ArrowDown|Escape|Esc|Enter|[,.\-+]|Tab/.test(key) &&
            !this.isPasting(evt))
            evt.preventDefault();

        if (key === 'ArrowUp') {
            evt.preventDefault();
            if (!isNaN(numValue)) {
                const step = Number(inputProps?.step || asCurrency ? .01 : 1);
                numValue += step;
                let stringValue = numValue.toString();
                if (asCurrency) stringValue = roundTo(numValue, 2);
                if (asNumber) this.change(Number(stringValue), evt);
                else this.change(stringValue, evt);
            }
            return;
        }
        if (key === 'ArrowDown') {
            evt.preventDefault();
            if (!isNaN(numValue)) {
                const step = Number(inputProps?.step || asCurrency ? .01 : 1);
                numValue -= step;
                let stringValue = numValue.toString();
                if (asCurrency) stringValue = roundTo(numValue, 2);
                if (asNumber) this.change(Number(stringValue), evt);
                else this.change(stringValue, evt);
            }
            return;
        }

        const textIsSelected = this.textIsSelected(evt), position = (target as HTMLInputElement).selectionStart;
        if (!textIsSelected) {
            if (/[,.]/.test(key) && /[,.]/.test(value.toString()) && !multipleSeparators)
                // Prevent double points or commas
                evt.preventDefault();

            if (wholeNumbers && /[.,]/.test(key))
                evt.preventDefault();
            else if (asCurrency && /[.,]\d{2}/.test(value.toString()) && /\d/.test(key) &&
                this.positionIsAfterComma(position, value.toString()))
                // Prevent more than 2 decimals when working with currencies
                evt.preventDefault();

            if ((position && position !== 0) && /[\-+]/.test(key))
                // Prevent - or + sign after first position
                evt.preventDefault();
        }

        if (onKeyDown !== undefined && onKeyDown !== null)
            onKeyDown(evt);
    };

    onChange = (value: string, evt: React.ChangeEvent) => {
        const {asNumber} = this.props;
        if (!/^-?[\d.,]*$/.test(value)) evt.preventDefault();
        else {
            let changeValue: string | number = value;
            if (value.startsWith('.') || value.startsWith(',')) value = 0 + value;
            if (asNumber) changeValue = Number(value.replace(',', '.'));
            this.change(changeValue, evt);
        }
    };

    change = (value: string | number, evt: React.ChangeEvent | React.KeyboardEvent) => {
        this.setState({value}, () => {
            this.props.onChange(value, evt as React.ChangeEvent);
        })
    };

    isPasting = (evt: React.KeyboardEvent) => {
        return (evt.metaKey || evt.ctrlKey) && evt.key === 'v';
    };

    positionIsAfterComma = (position: number | null, value: string) => {
        if (position === null) return true;
        if (value.includes('.')) return position > value.indexOf('.');
        if (value.includes(',')) return position > value.indexOf(',');
        return false;
    };

    textIsSelected = (evt: React.KeyboardEvent) => {
        const target = evt.target as HTMLInputElement, start = target.selectionStart, end = target.selectionEnd;
        return start != end;
    };

    render = () => {
        const value = this.props.unControlled ? this.state.value : this.props.value;
        return (
            <DvrdInputController {...this.props} value={value} onChange={this.onChange} onKeyDown={this.onKeyDown}
                                 unControlled={false}/>
        );
    }
}